export type Scalar = string | number | boolean;

export interface WaitCall {
  tool: string;
  provider: string;
  inputs: Record<string, Scalar>;
}

export interface WaitAction extends WaitCall {
  id: string;
}

export type WaitConditionOp = 'equals' | 'not_equals' | 'in' | 'exists' | 'not_exists';

export interface WaitCondition {
  action: string;
  field: string;
  op: WaitConditionOp;
  value?: Scalar | Scalar[];
}

export type WaitOutcome =
  | { do: 'compose'; instruction: string; next?: string }
  | { do: 'run'; actions: WaitCall[]; next?: string }
  | { do: 'wait'; step: string }
  | { do: 'handoff'; note?: string }
  | { do: 'end' };

export interface WaitStep {
  id: string;
  afterMinutes: number;
  actions: WaitAction[];
  when: { if: WaitCondition; then: WaitOutcome }[];
  otherwise: WaitOutcome;
}

export interface WaitProgram {
  version: 'wait.v1';
  kind: 'reply' | 'event';
  start: string;
  steps: WaitStep[];
  expiry?: { afterMinutes: number; then: WaitOutcome };
  namedValues: Record<string, string>;
}

export const FOLLOWUP_WAIT_TOOL = 'followup_wait';
export const FOLLOWUP_WAIT_PROVIDER = 'kim';

export const WAIT_STEP_MINUTES_MIN = 5;
export const WAIT_STEP_MINUTES_MAX = 43200; // 30 days, the same limit the backend enforces
/** What the Wait block's rail edits: one wait, then either a nudge or the end. */
export interface SimpleWait {
  minutes: number;
  nudge: boolean;
  prompt: string;
}

/** New waits: 2 days, nudge on, prompt still to be written. */
export const DEFAULT_SIMPLE_WAIT: SimpleWait = { minutes: 2 * 24 * 60, nudge: true, prompt: '' };

export function toSimpleWaitProgram({ minutes, nudge, prompt }: SimpleWait): WaitProgram {
  return {
    version: 'wait.v1',
    kind: 'reply',
    start: 'wait',
    namedValues: {},
    steps: [
      {
        id: 'wait',
        afterMinutes: minutes,
        actions: [],
        when: [],
        otherwise: nudge ? { do: 'compose', instruction: prompt.trim() } : { do: 'end' }
      }
    ]
  };
}

/** The start step's duration and nudge; for richer programs the rest is not shown. */
export function fromWaitProgram(program: WaitProgram): SimpleWait {
  const start = program.steps.find((step) => step.id === program.start) ?? program.steps[0];
  if (!start) return { ...DEFAULT_SIMPLE_WAIT };
  const otherwise = start.otherwise;
  return otherwise.do === 'compose'
    ? { minutes: start.afterMinutes, nudge: true, prompt: otherwise.instruction ?? '' }
    : { minutes: start.afterMinutes, nudge: false, prompt: '' };
}

export function defaultWaitProgram(): WaitProgram {
  return toSimpleWaitProgram(DEFAULT_SIMPLE_WAIT);
}

export type WaitUnit = 'minutes' | 'hours' | 'days';

const UNIT_MINUTES: Record<WaitUnit, number> = { minutes: 1, hours: 60, days: 1440 };

/** The largest unit that divides the duration evenly. */
export function splitWaitMinutes(minutes: number): { amount: number; unit: WaitUnit } {
  if (minutes >= 1440 && minutes % 1440 === 0) return { amount: minutes / 1440, unit: 'days' };
  if (minutes >= 60 && minutes % 60 === 0) return { amount: minutes / 60, unit: 'hours' };
  return { amount: minutes, unit: 'minutes' };
}

export const waitUnitMinutes = (amount: number, unit: WaitUnit): number => amount * UNIT_MINUTES[unit];

/** Minutes for an amount typed in a unit, or null while it is not a whole duration in range. */
export function typedWaitMinutes(amount: string, unit: WaitUnit): number | null {
  const value = Number(amount);
  if (amount.trim() === '' || !Number.isInteger(value)) return null;
  const minutes = waitUnitMinutes(value, unit);
  return minutes >= WAIT_STEP_MINUTES_MIN && minutes <= WAIT_STEP_MINUTES_MAX ? minutes : null;
}

export function formatWaitDuration(minutes: number): string {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    const days = minutes / 1440;
    return days === 1 ? '1 day' : `${days} days`;
  }
  if (minutes >= 60 && minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}

/** The chip's duration: "25 mins", "1 hour", "3 days". Minutes are never below 5, so always plural. */
export function formatWaitShort(minutes: number): string {
  const { unit } = splitWaitMinutes(minutes);
  return unit === 'minutes' ? `${minutes} mins` : formatWaitDuration(minutes);
}

/** Chip label, e.g. "Wait for a reply : 25 mins • Nudge". */
export function waitChipLabel(program: WaitProgram): string {
  const { minutes, nudge } = fromWaitProgram(program);
  return `Wait for a reply : ${formatWaitShort(minutes)}${nudge ? ' • Nudge' : ''}`;
}

/** Read a WaitProgram out of chip staticInputs, or null. */
export function readWaitProgram(staticInputs: unknown): WaitProgram | null {
  if (typeof staticInputs !== 'object' || staticInputs === null) return null;
  const program = (staticInputs as { program?: unknown }).program;
  if (typeof program !== 'object' || program === null) return null;
  const p = program as WaitProgram;
  if (p.version !== 'wait.v1') return null;
  return p;
}

export function clampWaitMinutes(minutes: number, min: number, max: number): number {
  if (!Number.isFinite(minutes)) return min;
  return Math.min(max, Math.max(min, Math.round(minutes)));
}
